import { useMutation, useQuery, useQueryCache } from "@pinia/colada";
import {
	CreateSite,
	type Site,
	type UpdateSite,
	useApi,
	UserScope,
} from "@soft-db/monitoring-api-client";
import { usePermissionStore } from "../stores/permission";
import {
	composeCacheEffects,
	QueryOptions,
	removeUndefinedFromListQuery,
	useCachedEntries,
	useInvalidator,
	useQueryEnabled,
	useScopeDataUpdater,
	useScopedQuery,
} from "./query";
import { stationsKeys } from "./station-query";

// --- Keys ---

export const sitesKeys = {
	root: ["sites"] as const,

	/**
	 * Root to easily invalidate all list queries,
	 * without invalidating individual site queries
	 */
	list: () => [...sitesKeys.root, "list"] as const,

	/**
	 * The default collection for the user
	 */
	byContext: () => [...sitesKeys.list(), "context"] as const,
	byFavorites: () => [...sitesKeys.list(), "favorites"] as const,

	byDist: (dist: string) => [...sitesKeys.list(), "dist", dist] as const,
	bySpace: (space: string) => [...sitesKeys.list(), "space", space] as const,

	byPublicPage: (page: string) => [...sitesKeys.list(), "public-page", page] as const,

	// We are using scopeKey, since recipients list will change based on the current scope
	byId: (id: string) => [...sitesKeys.root, "id", id] as const,

	file: (id: string, file: string) => ["site-file", id, file] as const,
	archives: (id: string) => ["site-archive", id] as const,
};

// --- Cache effects ---

function useUpdateCacheById() {
	const update = useScopeDataUpdater();

	return (site: Site) => {
		update(sitesKeys.byId(site.id), site);
	};
}

function useInvalidateListCache() {
	const invalidate = useInvalidator();

	return (site: Site) => {
		invalidate({
			keys: [sitesKeys.list(), stationsKeys.root],
			otherScopeKeys: [sitesKeys.byId(site.id)],
		});
	};
}

/**
 * Compose both updateCacheById and invalidateListCache effects
 */
function useSiteCacheEffects() {
	return composeCacheEffects(useInvalidateListCache(), useUpdateCacheById());
}

// --- Queries ---

function useSiteEnabled(...args: MaybeRefOrGetter<string | boolean>[]) {
	const { hasPermission } = usePermissionStore();

	return useQueryEnabled(...args, () => hasPermission("site.get"));
}

export function useSite(id: MaybeRefOrGetter<string>) {
	const api = useApi().site();

	const enabled = useSiteEnabled(id);
	const { data: site, ...rest } = useScopedQuery({
		key: () => sitesKeys.byId(toValue(id)),
		query: () => api.fetch(toValue(id)),
		enabled,
	});

	return { site, ...rest };
}

export function useSiteFile(siteId: MaybeRefOrGetter<string>, fileId: MaybeRefOrGetter<string>) {
	const api = useApi().site();

	const { data: file, ...rest } = useScopedQuery({
		key: () => sitesKeys.file(toValue(siteId), toValue(fileId)),
		query: () => api.fetchFile(toValue(siteId), toValue(fileId)),
		enabled: useSiteEnabled(siteId, fileId),
		staleTime: 60 * 60 * 1_000,
		placeholderData: (p) => p,
	});

	return {
		file,
		...rest,
	};
}

export function useQuickAccessSites(_isFavorite: MaybeRefOrGetter<boolean>) {
	const api = useApi().site();

	const isFavorite = computed(() => toValue(_isFavorite));

	const enabled = useSiteEnabled();

	const { data: sites, ...rest } = useScopedQuery({
		key: () => (isFavorite.value ? sitesKeys.byFavorites() : sitesKeys.byContext()),
		query: () => (isFavorite.value ? api.fetchFavorites() : api.fetchContext()),
		enabled,
	});

	return {
		sites: removeUndefinedFromListQuery(sites),
		...rest,
	};
}

export function useSites(opts?: QueryOptions) {
	const api = useApi().site();

	const enabled = useSiteEnabled(opts?.enabled || true);

	const { data: sites, ...rest } = useScopedQuery({
		key: () => sitesKeys.byContext(),
		query: () => api.fetchContext(),
		enabled,
	});

	return {
		sites: removeUndefinedFromListQuery(sites),
		data: sites,
		...rest,
	};
}

export function useSpaceSites(id: MaybeRefOrGetter<string>) {
	const api = useApi().site();

	const { data: sites, ...rest } = useQuery({
		key: () => sitesKeys.bySpace(toValue(id)),
		query: () => api.fetchSpace(toValue(id)),
		enabled: useQueryEnabled(id),
	});

	return { sites, ...rest };
}

export function usePublicPageSites(id: MaybeRefOrGetter<string>) {
	const api = useApi().site();

	const { data: sites, ...rest } = useScopedQuery({
		key: () => sitesKeys.byPublicPage(toValue(id)),
		query: () => api.fetchForPublicPage(toValue(id)),
		enabled: useQueryEnabled(id),
	});

	return { sites, ...rest };
}

export function useScopeSites(scopeRef: MaybeRefOrGetter<UserScope>) {
	const api = useApi().site();

	const { data: sites, ...rest } = useQuery({
		key: () => {
			const scope = toValue(scopeRef);

			switch (scope.type) {
				case "distributor":
					return sitesKeys.byDist(scope.id);
				case "space":
					return sitesKeys.bySpace(scope.id);
				case "none":
					return ["/dev/null"];
			}
		},
		query: async () => {
			const scope = toValue(scopeRef);
			switch (scope.type) {
				case "distributor":
					return api.fetchDist(scope.id);
				case "space":
					return api.fetchSpace(scope.id);
				case "none":
					return [];
			}
		},
		enabled: useQueryEnabled(() => !!toValue(scopeRef)),
	});

	return { sites, ...rest };
}

export function useSiteArchives(id: MaybeRefOrGetter<string>) {
	const api = useApi().archive();

	const { data: archives, ...rest } = useScopedQuery({
		key: () => sitesKeys.archives(toValue(id)),
		query: () => api.getAllTasks(toValue(id)),
		enabled: useQueryEnabled(id),
	});

	return {
		archives,
		...rest,
	};
}

export function useCachedSites() {
	return useCachedEntries<Site>(sitesKeys.root);
}

// --- Mutations ---

export function useCreateSite() {
	const api = useApi().site();

	return useMutation({
		mutation: ({ scope, data }: { scope: UserScope; data: CreateSite }) => {
			if (scope.type === "distributor") {
				return api.createWithDist(scope.id, data);
			} else if (scope.type === "space") {
				return api.createWithSpace(scope.id, data);
			}

			throw new Error("invalid scope for site creation");
		},
		onSuccess: useSiteCacheEffects(),
	});
}

export function useUpdateSiteImage() {
	const api = useApi().site();

	return useMutation({
		mutation: ({ id, image }: { id: string; image: File }) => api.updateImage(id, image),
		onSuccess: useUpdateCacheById(),
	});
}

export function useUpdateSite() {
	const api = useApi().site();

	return useMutation({
		mutation: ({ id, data }: { id: string; data: UpdateSite }) => api.update(id, data),
		onSuccess: useSiteCacheEffects(),
	});
}

export function useArchiveSite() {
	const api = useApi().site();

	return useMutation({
		mutation: (id: string) => api.archive(id),
		onSuccess: useSiteCacheEffects(),
	});
}

export function useRestoreSite() {
	const api = useApi().site();

	return useMutation({
		mutation: (id: string) => api.restore(id),
		onSuccess: useSiteCacheEffects(),
	});
}

export function useDeleteSiteImage() {
	const api = useApi().site();

	return useMutation({
		mutation: (id: string) => api.deleteImage(id),
		onSuccess: useUpdateCacheById(),
	});
}

export function useCreateSiteArchive() {
	const api = useApi().archive();

	const cache = useQueryCache();

	return useMutation({
		mutation: ({ id, startTime, endTime }: { id: string; startTime: Date; endTime: Date }) =>
			api.createTask(id, startTime, endTime),
		onSuccess: (_, { id }) => {
			cache.invalidateQueries({ key: sitesKeys.archives(id) });
		},
	});
}
